import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBell, FaCheck } from 'react-icons/fa';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import './NotificationBell.css';

const POLL_MS = 30_000;

const fmt = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)       return 'just now';
  if (diff < 3600)     return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400)    return `${Math.floor(diff / 3600)} h ago`;
  if (diff < 86400*7)  return `${Math.floor(diff / 86400)} d ago`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

function NotificationBell() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const canRead = hasPermission(user, 'user_notifications.read');

  const [unread, setUnread]   = useState(0);
  const [open, setOpen]       = useState(false);
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const btnRef   = useRef(null);

  // Poll unread count
  const fetchCount = useCallback(async () => {
    if (!canRead) return;
    try {
      const r = await axios.get('/notifications/unread-count');
      setUnread(r.data?.unread || 0);
    } catch (e) {
      // Silent — the bell is a polite background widget.
    }
  }, [canRead]);

  useEffect(() => {
    if (!canRead) return undefined;
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(t);
  }, [canRead, fetchCount]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/notifications?limit=20');
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          btnRef.current   && !btnRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const togglePanel = () => {
    if (!open) fetchList();
    setOpen(o => !o);
  };

  const markRead = async (n) => {
    try {
      if (!n.read_at) {
        await axios.post(`/notifications/${n.id}/mark-read`);
        setItems(items => items.map(i => i.id === n.id ? { ...i, read_at: new Date().toISOString() } : i));
        setUnread(u => Math.max(0, u - 1));
      }
    } catch (e) { /* ignore */ }
  };

  const onClick = (n) => {
    markRead(n);
    if (n.link_path) navigate(n.link_path);
    setOpen(false);
  };

  const markAllRead = async () => {
    try {
      await axios.post('/notifications/mark-all-read');
      setItems(items => items.map(i => i.read_at ? i : { ...i, read_at: new Date().toISOString() }));
      setUnread(0);
    } catch (e) { /* ignore */ }
  };

  if (!user || !canRead) return null;

  return (
    <div className="notif-bell-wrap">
      <button
        ref={btnRef}
        type="button"
        className="notif-bell-btn"
        onClick={togglePanel}
        title={unread > 0 ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'Notifications'}
        aria-label="Notifications"
      >
        <FaBell size={18} aria-hidden="true" />
        {unread > 0 && (
          <span className="notif-bell-badge">{unread > 99 ? '99+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel" ref={panelRef}>
          <div className="notif-panel__head">
            <strong>Notifications</strong>
            {unread > 0 && (
              <button type="button" className="notif-mark-all" onClick={markAllRead} title="Mark all as read">
                <FaCheck /> Mark all read
              </button>
            )}
          </div>

          <div className="notif-panel__body">
            {loading && <div className="notif-empty">Loading…</div>}
            {!loading && items.length === 0 && (
              <div className="notif-empty">You're all caught up.</div>
            )}
            {!loading && items.map(n => (
              <div
                key={n.id}
                className={`notif-item notif-item--sev-${n.severity || 'info'} ${n.read_at ? '' : 'is-unread'}`}
                onClick={() => onClick(n)}
              >
                <div className="notif-item__title">{n.title}</div>
                {n.body && <div className="notif-item__body">{n.body}</div>}
                <div className="notif-item__meta">
                  <span>{n.type.replace(/[._]/g, ' ')}</span>
                  <span>{fmt(n.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
